import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 3000;

export function BankTransactionEntityBackfillPanel() {
  const [previewPolling, setPreviewPolling] = useState(false);
  const [applyPolling, setApplyPolling] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    setPreviewPolling(false);
    setApplyPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: check for a previous apply job so returning users see the
  // persisted result, and so the daily scheduled run is visible here too.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.bankTransactionEntityBackfillJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          // Seed from the job's own started_at, not the moment this tab
          // happened to mount -- otherwise a page refresh mid-run resets
          // the elapsed-seconds display to 0 instead of the true runtime.
          var startedAtMs = job.started_at ? new Date(job.started_at).getTime() : NaN;
          startTimeRef.current = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();
          startPolling(job._id, 'apply');
        } else if (job.status === 'failed') {
          setApplyResult({ _failed: true, error: job.error });
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id, operation) => {
    stopPolling();
    if (operation === 'preview') setPreviewPolling(true);
    else setApplyPolling(true);

    startTimeRef.current = startTimeRef.current ?? Date.now();
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.bankTransactionEntityBackfillJobStatus(id);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'preview') {
            setPreview(data.result);
            toast.success('Preview complete');
          } else {
            setApplyResult(data.result);
            toast.success(`Backfill complete: ${data.result?.written ?? 0} corrections written`);
            setPreview(null);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.bankTransactionEntityBackfillPreviewStart();
      startPolling(data.job_id, 'preview');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.proposed_count === 0) return;
    if (!window.confirm(
      `This will stamp entity_id on ${preview.proposed_count} bank transaction(s) ` +
      `whose account is now assigned. Continue?`
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.bankTransactionEntityBackfillApplyStart();
      startPolling(data.job_id, 'apply');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || previewPolling || applyPolling;
  const canApply = preview && preview.proposed_count > 0 && !busy;

  return (
    <Card data-testid="bank-transaction-entity-backfill-panel">
      <CardHeader>
        <CardTitle className="text-lg">Bank Transaction entity_id Backfill</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Writes entity_id on bank transactions whose parent account is now
          assigned but the transaction itself synced before that assignment
          (or fell outside its feed's resync window since). Also runs daily
          at 05:45 UTC as a safety net. Preview and Apply both re-classify
          fresh -- idempotent, never touches an account still unassigned.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Button onClick={runPreview} disabled={busy} size="sm" data-testid="backfill-preview-button">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply}
            size="sm"
            variant={canApply ? 'default' : 'outline'}
            data-testid="backfill-apply-button"
          >
            {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply ${fmtInt(preview.proposed_count)} correction(s)` : 'Apply'}
          </Button>
          {(previewPolling || applyPolling) && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {elapsed}s
            </span>
          )}
        </div>

        {preview && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only)
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Missing entity_id</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(preview.total_missing_entity_transactions)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Derivable (proposed)</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.proposed_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Account still unassigned</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.account_unassigned_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">No account map</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.no_account_map_count)}</p>
              </div>
            </div>

            {preview.proposed_corrections && preview.proposed_corrections.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Proposed corrections ({preview.proposed_corrections.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">transaction_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">source</th>
                        <th className="text-left px-2 py-1.5 font-medium">correct entity_id</th>
                        <th className="text-right px-2 py-1.5 font-medium">amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.proposed_corrections.map((w, i) => (
                        <tr key={w.transaction_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.transaction_id}</td>
                          <td className="px-2 py-1">{w.source}</td>
                          <td className="px-2 py-1 font-mono text-green-600">{w.correct_entity_id}</td>
                          <td className="px-2 py-1 text-right font-mono">{w.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
          </div>
        )}

        {applyResult && !applyResult._failed && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Written</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.written)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Conflicts skipped</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(applyResult.skipped_conflict)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Account still unassigned</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.account_unassigned_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">No account map</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.no_account_map_count)}</p>
              </div>
            </div>

            {applyResult.written_samples && applyResult.written_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Written samples ({applyResult.written_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">transaction_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">previous</th>
                        <th className="text-left px-2 py-1.5 font-medium">corrected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyResult.written_samples.map((w, i) => (
                        <tr key={w.transaction_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{w.transaction_id}</td>
                          <td className="px-2 py-1 font-mono text-red-600">{w.previous_entity_id || '—'}</td>
                          <td className="px-2 py-1 font-mono text-green-600">{w.corrected_entity_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Run: {applyResult.run_id} | Generated: {applyResult.generated_at}
            </p>
          </div>
        )}

        {applyResult && applyResult._failed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIOUS APPLY FAILED
            </Badge>
            <p className="text-xs text-red-600">{applyResult.error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
